// src/services/productos.service.js - VERSIÓN CORREGIDA COMPLETA CON MÉTODOS FALTANTES
import FirebaseService, { ensureArray as ensureArrayPayload, ensureObject as ensureObjectPayload } from './firebase.service';
import ApiService from './api.service';
import { normalizePluBalanza, productMatchesPlu } from '../utils/balanza/pluBalanza';
import { roundStockQty } from '../utils/stockQty';

/**
 * Servicio para gestión de productos con Firebase
 * ✅ VERSIÓN CORREGIDA - Incluye métodos faltantes para stock por sucursal
 * Mantiene EXACTAMENTE la misma interfaz que el servicio original
 */
class ProductosService extends FirebaseService {
  constructor() {
    super('/productos'); // Módulo en Firebase Functions
    this.api = new ApiService('/productos');
  }

  getOrgQuery(params = {}) {
    // Obtener orgId del localStorage o del contexto global si está disponible
    const hasWindow = typeof window !== 'undefined';
    const storage = hasWindow ? window.localStorage : null;
    const authContext = hasWindow ? window.authContext : null;

    const contextOrgId =
      authContext?.orgId ||
      authContext?.currentUser?.orgId ||
      null;

    const storedOrgId =
      storage?.getItem?.('orgId') ||
      storage?.getItem?.('companyId') ||
      null;

    const orgId = contextOrgId || storedOrgId || null;
    return orgId ? { ...params, orgId } : params;
  }

  async obtenerTodos() {
    try {
      console.log('[PRODUCTOS SERVICE] Obteniendo todos los productos…');
      const response = await this.api.get('', this.getOrgQuery());
      const productosArray = ensureArrayPayload(response?.data);

      console.log('Productos obtenidos correctamente:', productosArray.length);
      return productosArray;
    } catch (error) {
      console.error('[PRODUCTOS SERVICE] Error al obtener productos:', error);
      throw error;
    }
  }

  async obtenerTodas() { return this.obtenerTodos(); }

  async obtenerActivos() {
    try {
      const response = await this.api.get('/activos', this.getOrgQuery());
      const productosArray = ensureArrayPayload(response?.data);
      return productosArray;
    } catch (error) {
      console.error('Error al obtener productos activos:', error);
      throw error;
    }
  }

  async obtenerPorId(id) {
    try {
      const response = await this.api.get(`/${id}`, this.getOrgQuery());
      const productoObj = ensureObjectPayload(response?.data);
      if (!productoObj || Object.keys(productoObj).length === 0) {
        throw new Error(`Producto ${id} no encontrado`);
      }
      return productoObj;
    } catch (error) {
      console.error(`❌ Error al obtener producto ${id}:`, error);
      throw error;
    }
  }

  async buscarConStockPorSucursal(termino, sucursalId) {
    try {
      if (!sucursalId) throw new Error('ID de sucursal es requerido');
      const queryParams = this.getOrgQuery(
        termino && termino.trim() ? { termino: termino.trim() } : {}
      );
      const response = await this.api.get(`/buscar-con-stock/${sucursalId}`, queryParams);
      const productosArray = ensureArrayPayload(response?.data);
      const productosNormalizados = productosArray.map(p => ({
        ...p,
        stock_sucursal: parseFloat(p?.stock_sucursal ?? p?.cantidad ?? p?.stock ?? p?.stock_actual ?? 0) || 0,
        stock_actual: parseFloat(p?.stock_sucursal ?? p?.cantidad ?? p?.stock ?? p?.stock_actual ?? 0) || 0,
        stock_minimo: parseFloat(p?.stock_minimo ?? 5) || 5,
        sucursal_id: p?.sucursal_id ?? sucursalId
      }));
      const conStock = productosNormalizados.filter(p => (parseFloat(p.stock_sucursal ?? p.stock_actual ?? 0) || 0) > 0);
      if (conStock.length === 0 && termino) return await this.buscarConStockFallback(termino, sucursalId);
      return productosNormalizados;
    } catch (error) {
      console.error('[PRODUCTOS SERVICE] Error al buscar productos con stock por sucursal:', error);
      return await this.buscarConStockFallback(termino, sucursalId);
    }
  }

  async buscarConStockFallback(termino, sucursalId) {
    try {
      const productos = await this.buscar(termino, sucursalId);
      if (productos.length === 0) return [];
      const productosConStock = await Promise.all(
        productos.map(async (producto) => {
          try {
            const stockSucursal = await this.consultarStockEnSucursal(producto.id, sucursalId);
            const stockResuelto = parseFloat(stockSucursal.cantidad ?? stockSucursal.stock ?? 0) || 0;
            return {
              ...producto,
              stock_actual: stockResuelto,
              stock_sucursal: stockResuelto,
              stock_minimo: stockSucursal.stock_minimo || producto.stock_minimo || 5,
              sucursal_id: sucursalId
            };
          } catch (error) {
            const stockFallback = parseFloat(producto.stock_sucursal ?? producto.stock_actual ?? producto.stock?.cantidad ?? 0) || 0;
            return { ...producto, stock_actual: stockFallback, stock_sucursal: stockFallback, sucursal_id: sucursalId };
          }
        })
      );
      const terminoLower = String(termino || '').trim().toLowerCase();
      return productosConStock
        .filter((producto) => {
          if (!terminoLower) return true;
          const nombre = String(producto.nombre || '').toLowerCase();
          const codigo = String(producto.codigo || '').toLowerCase();
          const codigoBarras = String(producto.codigo_barras || '').toLowerCase();
          return nombre.includes(terminoLower) || codigo.includes(terminoLower) || codigoBarras.includes(terminoLower);
        })
        .sort((a, b) => (parseFloat(b.stock_sucursal || 0) || 0) - (parseFloat(a.stock_sucursal || 0) || 0));
    } catch (error) {
      console.error('❌ [PRODUCTOS SERVICE] Error en fallback:', error);
      return [];
    }
  }

  async consultarStockEnSucursal(productoId, sucursalId) {
    try {
      const stockService = new FirebaseService('/stock-sucursal');
      const response = await stockService.get(
        `/consolidado/${sucursalId}/${productoId}`,
        this.getOrgQuery()
      );
      const row = ensureObjectPayload(response?.data);
      const cantidad = parseFloat(row?.cantidad ?? row?.stock ?? row?.stock_actual ?? 0) || 0;
      const stockMin = parseFloat(row?.stock_minimo ?? 5) || 5;
      return { stock: cantidad, cantidad, stock_minimo: stockMin };
    } catch (error) {
      return { stock: 0, cantidad: 0, stock_minimo: 5 };
    }
  }

  async revalidarStockItemPorCodigo(item, sucursalId) {
    // C1B: Combos usan stock virtual de componentes — no reconsultar como producto
    if (item?.es_combo === true || String(item?.tipoVenta || '').trim() === 'combo') {
      const stock = parseFloat(item?.stock_disponible ?? item?.stock_actual ?? 0) || 0;
      return { ...item, stock_disponible: stock, stock_actual: stock, stock_sucursal: stock };
    }
    const codigo = item?.codigo;
    if (!codigo || !sucursalId) {
      const stock = parseFloat(item?.stock_disponible ?? item?.stock_actual ?? 0) || 0;
      return { ...item, stock_disponible: stock, stock_actual: stock, stock_sucursal: stock };
    }
    const producto = await this.obtenerPorCodigoConStock(codigo, sucursalId);
    if (!producto) return null;
    const stock = parseFloat(producto.stock_actual ?? producto.stock_sucursal ?? 0) || 0;
    return {
      ...item,
      stock_disponible: stock,
      stock_actual: stock,
      stock_sucursal: stock
    };
  }

  async revalidarStockCarrito(carrito, sucursalId) {
    const items = Array.isArray(carrito) ? carrito : [];
    const actualizado = [];
    for (const item of items) {
      const refreshed = await this.revalidarStockItemPorCodigo(item, sucursalId);
      if (!refreshed) return { ok: false, item, carrito: items };
      actualizado.push(refreshed);
    }
    return { ok: true, carrito: actualizado };
  }

  async obtenerPorCodigoConStock(codigo, sucursalId) {
    try {
      if (!codigo || !sucursalId) throw new Error('Código de producto y ID de sucursal son requeridos');
      try {
        const url = `/codigo/${encodeURIComponent(codigo)}/sucursal/${sucursalId}`;
        const response = await this.api.get(url, this.getOrgQuery());
        return ensureObjectPayload(response?.data);
      } catch (endpointError) {
        const producto = await this.obtenerPorCodigo(codigo);
        if (!producto) return null;
        const stockSucursal = await this.consultarStockEnSucursal(producto.id, sucursalId);
        const stockResuelto = parseFloat(stockSucursal.cantidad ?? stockSucursal.stock ?? 0) || 0;
        return { ...producto, stock_actual: stockResuelto, stock_sucursal: stockResuelto, stock_minimo: stockSucursal.stock_minimo || producto.stock_minimo || 5, sucursal_id: sucursalId };
      }
    } catch (error) {
      console.error('[PRODUCTOS SERVICE] Error al buscar producto por código con stock:', error);
      return null;
    }
  }

  /** Busca producto de balanza por PLU (campo pluBalanza o codigo = PLU). Solo cliente/API existente. */
  async obtenerPorPluBalanzaConStock(plu, sucursalId) {
    const pluNorm = normalizePluBalanza(plu);
    if (!pluNorm || !sucursalId) return null;

    try {
      const porCodigo = await this.obtenerPorCodigoConStock(pluNorm, sucursalId);
      if (porCodigo?.id && productMatchesPlu(porCodigo, pluNorm)) {
        return porCodigo;
      }

      const candidatos = await this.buscar(pluNorm, sucursalId);
      const match = (Array.isArray(candidatos) ? candidatos : []).find(
        (p) => productMatchesPlu(p, pluNorm)
      );
      if (!match?.id) return null;

      const clave = String(match.codigo || pluNorm).trim();
      return this.obtenerPorCodigoConStock(clave, sucursalId);
    } catch (error) {
      console.error('[PRODUCTOS SERVICE] Error al buscar producto por PLU balanza:', error);
      return null;
    }
  }

  async buscar(termino, sucursalId = null) {
    try {
      const params = this.getOrgQuery({ search: termino, limit: 20, ...(sucursalId && { sucursal_id: sucursalId }) });
      const response = await this.api.get('', params);
      return ensureArrayPayload(response?.data);
    } catch (error) {
      console.error('Error al buscar productos:', error);
      return [];
    }
  }

  async obtenerPorCodigo(codigo) {
    try {
      const productos = await this.buscar(codigo);
      if (productos && productos.length > 0) {
        const productoExacto = productos.find(p => p.codigo === codigo || p.codigo_barras === codigo || p.codigo?.toString() === codigo?.toString());
        if (productoExacto) return productoExacto;
        return productos[0];
      }
      return null;
    } catch (error) {
      console.error(`❌ Error al buscar por código ${codigo}:`, error);
      return null;
    }
  }

  /**
   * Verifica en Firebase si la empresa ya tiene un producto con ese código o código de barras (GTIN normalizado).
   * @returns {Promise<{ registrado: boolean, productoId: string|null }>}
   */
  async verificarRegistradoPorCodigo(codigo) {
    try {
      const c = String(codigo || '').trim();
      if (!c) return { registrado: false, productoId: null };
      const endpoint = `/registrado-por-codigo/${encodeURIComponent(c)}`;
      const response = await this.api.get(endpoint, this.getOrgQuery());
      const payload = response?.data?.data ?? response?.data;
      if (payload && typeof payload.registrado === 'boolean') {
        return { registrado: payload.registrado, productoId: payload.productoId || null };
      }
      return { registrado: false, productoId: null };
    } catch (error) {
      console.warn('[PRODUCTOS] verificarRegistradoPorCodigo:', error?.message || error);
      return { registrado: false, productoId: null };
    }
  }

  async crear(producto) {
    try {
      const productoFormateado = {
        ...producto,
        codigo: producto.codigo?.trim() || '',
        nombre: producto.nombre?.trim() || '',
        descripcion: producto.descripcion?.trim() || '',
        precio_costo: parseFloat(producto.precio_costo || 0),
        precio_venta: parseFloat(producto.precio_venta || 0),
        stock_actual: roundStockQty(producto.stock_actual || producto.stock_inicial || 0),
        stock_minimo: roundStockQty(producto.stock_minimo || 5),
        categoria_id: producto.categoria_id || '',
        proveedor_id: producto.proveedor_id || '',
        activo: producto.activo !== false
      };
      const resultado = await this.post('', this.getOrgQuery(productoFormateado));
      return resultado;
    } catch (error) {
      console.error('❌ Error al crear producto:', error);
      throw error;
    }
  }

  async actualizar(id, producto) {
    try {
      const productoFormateado = { ...producto };
      if (producto.precio_costo !== undefined) productoFormateado.precio_costo = parseFloat(producto.precio_costo || 0);
      if (producto.precio_venta !== undefined) productoFormateado.precio_venta = parseFloat(producto.precio_venta || 0);
      if (producto.stock_actual !== undefined) productoFormateado.stock_actual = roundStockQty(producto.stock_actual || 0);
      if (producto.stock_minimo !== undefined) productoFormateado.stock_minimo = roundStockQty(producto.stock_minimo || 5);
      const resultado = await this.put(`/${id}`, this.getOrgQuery(productoFormateado));
      return resultado;
    } catch (error) {
      console.error(`❌ Error al actualizar producto ${id}:`, error);
      throw error;
    }
  }

  async eliminar(id) {
    try {
      const resultado = await this.delete(`/${id}`);
      return resultado;
    } catch (error) {
      console.error(`❌ Error al eliminar producto ${id}:`, error);
      throw error;
    }
  }

  async obtenerStockBajo() {
    try {
      const response = await this.api.get('/stock-bajo', this.getOrgQuery());
      const productosArray = ensureArrayPayload(response?.data);
      return productosArray;
    } catch (error) {
      console.error('Error al obtener stock bajo:', error);
      throw error;
    }
  }

  async obtenerPorCategoria(categoriaId) {
    try {
      const response = await this.api.get('/categoria', this.getOrgQuery({ categoriaId }));
      const productosArray = ensureArrayPayload(response?.data);
      return productosArray;
    } catch (error) {
      console.error(`Error al obtener productos de categoría ${categoriaId}:`, error);
      throw error;
    }
  }

  async ajustarStock(id, cantidad, motivo = 'Ajuste manual') {
    try {
      const ajuste = { cantidad: roundStockQty(cantidad), motivo: motivo.trim() };
      const resultado = await this.post(`/${id}/stock`, this.getOrgQuery(ajuste));
      return resultado;
    } catch (error) {
      console.error(`❌ Error al ajustar stock del producto ${id}:`, error);
      throw error;
    }
  }

  async obtenerHistorialMovimientos(id) {
    try {
      const response = await this.api.get(`/${id}/movimientos`, this.getOrgQuery());
      return ensureArrayPayload(response?.data);
    } catch (error) {
      console.error(`Error al obtener historial del producto ${id}:`, error);
      return [];
    }
  }

  async obtenerEstadisticas() {
    try {
      const response = await this.api.get('/estadisticas', this.getOrgQuery());
      const statsObj = ensureObjectPayload(response?.data);
      if (!statsObj || Object.keys(statsObj).length === 0) {
        return { total: 0, activos: 0, stockBajo: 0, sinStock: 0 };
      }
      return statsObj;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      return { total: 0, activos: 0, stockBajo: 0, sinStock: 0 };
    }
  }

  async buscarConFiltros(filtros = {}) {
    try {
      const response = await this.api.get('/filtros', this.getOrgQuery(filtros));
      return ensureArrayPayload(response?.data);
    } catch (error) {
      console.error('Error en búsqueda con filtros:', error);
      throw error;
    }
  }

  async verificarDisponibilidad(id, cantidad) {
    try {
      const producto = await this.obtenerPorId(id);
      if (!producto) return { disponible: false, mensaje: 'Producto no encontrado', stockActual: 0 };
      const stockActual = roundStockQty(producto.stock_actual || 0);
      const disponible = stockActual >= cantidad;
      return { disponible, stockActual, cantidadSolicitada: cantidad, mensaje: disponible ? 'Stock disponible' : `Stock insuficiente. Disponible: ${stockActual}, Solicitado: ${cantidad}` };
    } catch (error) {
      console.error(`❌ Error al verificar disponibilidad del producto ${id}:`, error);
      return { disponible: false, mensaje: 'Error al verificar disponibilidad', stockActual: 0 };
    }
  }

  async importarMasivo(productos, sucursalId, opciones = {}) {
    try {
      const datosEnvio = {
        productos: productos.map(p => ({
          codigo: p.codigo?.toString().trim() || '',
          nombre: p.nombre?.toString().trim() || '',
          descripcion: p.descripcion?.toString().trim() || '',
          precio_costo: parseFloat(p.precio_costo || 0),
          precio_venta: parseFloat(p.precio_venta || 0),
          stock_inicial: roundStockQty(p.stock_actual || p.stock_inicial || 0),
          stock_minimo: roundStockQty(p.stock_minimo || 5),
          categoria_id: p.categoria_id || opciones.categoria_default || '',
          unidad_medida: p.unidad_medida || 'unidad',
          activo: p.activo !== false
        })),
        sucursal_id: sucursalId,
        opciones: {
          categoria_default: opciones.categoria_default || '',
          evitar_duplicados: opciones.evitar_duplicados || false,
          actualizar_existentes: opciones.actualizar_existentes || true
        }
      };
      const resultado = await this.api.post('/importar-masivo', this.getOrgQuery(datosEnvio));
      const payload = (resultado?.data?.data) ?? resultado?.data ?? resultado;
      const errores = payload?.errores ?? payload?.errors ?? [];
      return {
        success: true,
        message: resultado?.data?.message || 'Importación completada',
        data: {
          importados: payload?.importados ?? payload?.created ?? payload?.creados ?? 0,
          actualizados: payload?.actualizados ?? payload?.updated ?? 0,
          duplicados: payload?.duplicados ?? payload?.skipped ?? 0,
          errores: Array.isArray(errores) ? errores : (errores ? [errores] : []),
          total_enviados: productos.length,
          procesamiento_completo: payload?.procesamiento_completo ?? true
        }
      };
    } catch (error) {
      console.error('❌ [PRODUCTOS SERVICE] Error en importación masiva:', error);
      throw new Error('Error durante la importación masiva: ' + error.message);
    }
  }

  async obtenerMasVendidos(limite = 10) {
    try {
      const response = await this.api.get('/mas-vendidos', this.getOrgQuery({ limite }));
      return ensureArrayPayload(response?.data);
    } catch (error) {
      console.error('Error al obtener productos más vendidos:', error);
      return [];
    }
  }
}

export default new ProductosService();

export async function obtenerProductoParaVenta(codigo, sucursalId) {
  if (/\s|\./.test(codigo)) {
    const productosService = new ProductosService();
    const productos = await productosService.buscar(codigo, sucursalId);
    return productos.find(p => p.codigo === codigo) || productos[0] || null;
  } else {
    const productosService = new ProductosService();
    return await productosService.obtenerPorCodigoConStock(codigo, sucursalId);
  }
} 